import type { ParseKeys } from 'i18next'
import { DateUnitEnum } from 'shared/constants'
import { LocaleEnum, getLanguageCodeByLocale } from 'shared/enums/locale-enum'

export interface FormatCommunityRelativeDateOptions {
  locale?: string
  timeZone: string
  now?: Date
}

export interface FormatPublicationRelativeDateOptions extends FormatCommunityRelativeDateOptions {
  translate: (key: ParseKeys) => string
}

type PreviousYearFormat = 'month-day-year' | 'month-year'

const toIntlLocale = (locale?: string): string =>
  getLanguageCodeByLocale((locale ?? LocaleEnum.ENGLISH) as LocaleEnum)

const safeTimeZone = (timeZone: string): string => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format(0)
    return timeZone
  } catch {
    return 'UTC'
  }
}

const yearInTimeZone = (date: Date, timeZone: string): number => {
  const part = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric' })
    .formatToParts(date)
    .find(p => p.type === 'year')
  return Number(part?.value)
}

const communityCalendar = ({
  date,
  now,
  locale,
  timeZone,
  previousYear,
}: {
  date: Date
  now: Date
  locale: string | undefined
  timeZone: string
  previousYear: PreviousYearFormat
}): string => {
  const intlLocale = toIntlLocale(locale)
  const tz = safeTimeZone(timeZone)

  if (yearInTimeZone(date, tz) === yearInTimeZone(now, tz)) {
    return new Intl.DateTimeFormat(intlLocale, {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  if (previousYear === 'month-year') {
    return new Intl.DateTimeFormat(intlLocale, {
      timeZone: tz,
      month: 'short',
      year: 'numeric',
    }).format(date)
  }

  return new Intl.DateTimeFormat(intlLocale, {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export const formatPublicationRelativeDate = (
  date: Date,
  { locale, timeZone, now, translate }: FormatPublicationRelativeDateOptions,
): string => {
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const reference = now ?? new Date()
  const seconds = Math.max(0, Math.floor((reference.getTime() - date.getTime()) / 1000))

  if (seconds < DateUnitEnum.minute) {
    return translate('comments.components.comments.just_now_label')
  }
  if (seconds < DateUnitEnum.hour) {
    return `${Math.floor(seconds / DateUnitEnum.minute)}${translate('global.time.minutes_short')}`
  }
  if (seconds < DateUnitEnum.day) {
    return `${Math.floor(seconds / DateUnitEnum.hour)}${translate('global.time.hours_short')}`
  }
  if (seconds <= 30 * DateUnitEnum.day) {
    return `${Math.floor(seconds / DateUnitEnum.day)}${translate('global.time.days_short')}`
  }

  return communityCalendar({
    date,
    now: reference,
    locale,
    timeZone,
    previousYear: 'month-year',
  })
}

export const formatActivityRelativeDate = (
  date: Date,
  { locale, timeZone, now }: FormatCommunityRelativeDateOptions,
): string => {
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const reference = now ?? new Date()
  const seconds = Math.max(0, Math.floor((reference.getTime() - date.getTime()) / 1000))
  const intlLocale = toIntlLocale(locale)

  if (seconds < DateUnitEnum.minute) {
    return new Intl.RelativeTimeFormat(intlLocale, {
      numeric: 'auto',
      style: 'long',
    }).format(0, 'second')
  }

  if (seconds <= 30 * DateUnitEnum.day) {
    let value: number
    let unit: Intl.RelativeTimeFormatUnit

    if (seconds < DateUnitEnum.hour) {
      value = Math.floor(seconds / DateUnitEnum.minute)
      unit = 'minute'
    } else if (seconds < DateUnitEnum.day) {
      value = Math.floor(seconds / DateUnitEnum.hour)
      unit = 'hour'
    } else {
      value = Math.floor(seconds / DateUnitEnum.day)
      unit = 'day'
    }

    return new Intl.RelativeTimeFormat(intlLocale, {
      numeric: 'always',
      style: 'long',
    }).format(-value, unit)
  }

  return communityCalendar({
    date,
    now: reference,
    locale,
    timeZone,
    previousYear: 'month-day-year',
  })
}
